import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { message } from 'antd';
import {
  create_sieve_label,
  regenerate_missing_sieve_pdfs,
  get_sieve_labels,
  get_sieve_label,
  clean_sieve,
  allocate_sieve_batch,
  change_sieve_status,
  get_sieve_counts,
  get_sieve_logs,
  bulk_clean_sieves,
  bulk_allocate_sieves,
  bulk_status_change_sieves,
  generate_sieve_usage_log_report,
  bulkInactivateSievesApi,
  reactivateSieveApi
} from './api';

const initialState = {
  loading: false,
  list: [],
  listPagination: {},
  pendingCount: 0,
  current: null,
  counts: { to_be_cleaned: 0, cleaned: 0, in_use: 0, inactive: 0 },
  stalePendingCount: 0,
  sieveLogs: [],
  sieveLogsLoading: false,
  reportGenerating: false
};

const actions = {
  CREATE_SIEVE_LABEL: 'sieveLabel/CREATE_SIEVE_LABEL',
  REGENERATE_MISSING_SIEVE_PDFS: 'sieveLabel/REGENERATE_MISSING_SIEVE_PDFS',
  GET_SIEVE_LABELS: 'sieveLabel/GET_SIEVE_LABELS',
  GET_SIEVE_LABEL: 'sieveLabel/GET_SIEVE_LABEL',
  CLEAN_SIEVE: 'sieveLabel/CLEAN_SIEVE',
  ALLOCATE_SIEVE: 'sieveLabel/ALLOCATE_SIEVE',
  STATUS_CHANGE_SIEVE: 'sieveLabel/STATUS_CHANGE_SIEVE',
  GET_SIEVE_COUNTS: 'sieveLabel/GET_SIEVE_COUNTS',
  GET_SIEVE_LOGS: 'sieveLabel/GET_SIEVE_LOGS',
  BULK_CLEAN: 'sieveLabel/BULK_CLEAN',
  BULK_ALLOCATE: 'sieveLabel/BULK_ALLOCATE',
  BULK_STATUS_CHANGE: 'sieveLabel/BULK_STATUS_CHANGE',
  GENERATE_USAGE_LOG_REPORT: 'sieveLabel/GENERATE_USAGE_LOG_REPORT',
  BULK_INACTIVATE: 'sieveLabel/BULK_INACTIVATE',
  REACTIVATE_SIEVE: 'sieveLabel/REACTIVATE_SIEVE'
};

export const createSieveLabel = createAsyncThunk(
  actions.CREATE_SIEVE_LABEL,
  async (payload) => {
    return await create_sieve_label(payload);
  }
);

export const regenerateMissingSievePdfs = createAsyncThunk(
  actions.REGENERATE_MISSING_SIEVE_PDFS,
  async () => {
    return await regenerate_missing_sieve_pdfs();
  }
);

export const getSieveLabels = createAsyncThunk(
  actions.GET_SIEVE_LABELS,
  async (payload) => {
    return await get_sieve_labels(payload);
  }
);

export const getSieveLabel = createAsyncThunk(
  actions.GET_SIEVE_LABEL,
  async (id) => {
    return await get_sieve_label(id);
  }
);

export const cleanSieve = createAsyncThunk(actions.CLEAN_SIEVE, async (id) => {
  return await clean_sieve(id);
});

export const allocateSieve = createAsyncThunk(
  actions.ALLOCATE_SIEVE,
  async ({ id, values }) => {
    return await allocate_sieve_batch(id, values);
  }
);

export const statusChangeSieve = createAsyncThunk(
  actions.STATUS_CHANGE_SIEVE,
  async (id) => {
    return await change_sieve_status(id);
  }
);

export const getSieveCounts = createAsyncThunk(
  actions.GET_SIEVE_COUNTS,
  async () => {
    return await get_sieve_counts();
  }
);

export const getSieveLogs = createAsyncThunk(
  actions.GET_SIEVE_LOGS,
  async (sieveLabelNumber) => {
    return await get_sieve_logs(sieveLabelNumber);
  }
);

export const bulkCleanSieves = createAsyncThunk(
  actions.BULK_CLEAN,
  async (payload) => {
    return await bulk_clean_sieves(payload);
  }
);

export const bulkAllocateSieves = createAsyncThunk(
  actions.BULK_ALLOCATE,
  async (payload) => {
    return await bulk_allocate_sieves(payload);
  }
);

export const bulkStatusChangeSieves = createAsyncThunk(
  actions.BULK_STATUS_CHANGE,
  async (payload) => {
    return await bulk_status_change_sieves(payload);
  }
);

export const generateSieveUsageLogReport = createAsyncThunk(
  actions.GENERATE_USAGE_LOG_REPORT,
  async (payload) => {
    return await generate_sieve_usage_log_report(payload);
  }
);

export const bulkInactivateSieves = createAsyncThunk(
  actions.BULK_INACTIVATE,
  async (payload, { rejectWithValue }) => {
    try {
      return await bulkInactivateSievesApi(payload);
    } catch (err) {
      return rejectWithValue(err?.response?.data);
    }
  }
);

export const reactivateSieve = createAsyncThunk(
  actions.REACTIVATE_SIEVE,
  async (payload, { rejectWithValue }) => {
    try {
      return await reactivateSieveApi(payload);
    } catch (err) {
      return rejectWithValue(err?.response?.data);
    }
  }
);

const sieveLabelSlice = createSlice({
  name: 'sieveLabel',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(createSieveLabel.pending, (state) => {
        state.loading = true;
      })
      .addCase(createSieveLabel.fulfilled, (state, action) => {
        state.loading = false;
        const { success, message: msg } = action.payload || {};
        if (success) {
          message.success(msg);
        } else if (msg) {
          message.error(msg);
        }
      })
      .addCase(createSieveLabel.rejected, (state, action) => {
        state.loading = false;
        message.error(action.error?.message);
      });

    builder
      .addCase(regenerateMissingSievePdfs.pending, (state) => {
        state.loading = true;
      })
      .addCase(regenerateMissingSievePdfs.fulfilled, (state, action) => {
        state.loading = false;
        const { success, message: msg } = action.payload || {};
        if (success) {
          message.success(msg);
        } else if (msg) {
          message.error(msg);
        }
      })
      .addCase(regenerateMissingSievePdfs.rejected, (state, action) => {
        state.loading = false;
        message.error(action.error?.message);
      });

    builder
      .addCase(getSieveLabels.pending, (state) => {
        state.loading = true;
      })
      .addCase(getSieveLabels.fulfilled, (state, action) => {
        state.loading = false;
        const {
          success,
          data,
          pagination,
          pending_count,
          stale_pending_count,
          message: msg
        } = action.payload || {};
        if (success) {
          state.list = data || [];
          state.listPagination = pagination || {};
          state.pendingCount = pending_count || 0;
          state.stalePendingCount = stale_pending_count || 0;
        } else if (msg) {
          message.error(msg);
        }
      })
      .addCase(getSieveLabels.rejected, (state, action) => {
        state.loading = false;
        message.error(action.error?.message);
      });

    builder
      .addCase(getSieveLabel.pending, (state) => {
        state.loading = true;
      })
      .addCase(getSieveLabel.fulfilled, (state, action) => {
        state.loading = false;
        const { success, data, message: msg } = action.payload || {};
        if (success) {
          state.current = data;
        } else if (msg) {
          message.error(msg);
        }
      })
      .addCase(getSieveLabel.rejected, (state, action) => {
        state.loading = false;
        message.error(action.error?.message);
      });

    builder
      .addCase(cleanSieve.pending, (state) => {
        state.loading = true;
      })
      .addCase(cleanSieve.fulfilled, (state, action) => {
        state.loading = false;
        const { success, message: msg } = action.payload || {};
        if (success) message.success(msg);
        else if (msg) message.error(msg);
      })
      .addCase(cleanSieve.rejected, (state, action) => {
        state.loading = false;
        message.error(action.error?.message);
      });

    builder
      .addCase(allocateSieve.pending, (state) => {
        state.loading = true;
      })
      .addCase(allocateSieve.fulfilled, (state, action) => {
        state.loading = false;
        const { success, message: msg } = action.payload || {};
        if (success) message.success(msg);
        else if (msg) message.error(msg);
      })
      .addCase(allocateSieve.rejected, (state, action) => {
        state.loading = false;
        message.error(action.error?.message);
      });

    builder
      .addCase(statusChangeSieve.pending, (state) => {
        state.loading = true;
      })
      .addCase(statusChangeSieve.fulfilled, (state, action) => {
        state.loading = false;
        const { success, message: msg } = action.payload || {};
        if (success) message.success(msg);
        else if (msg) message.error(msg);
      })
      .addCase(statusChangeSieve.rejected, (state, action) => {
        state.loading = false;
        message.error(action.error?.message);
      });

    builder
      .addCase(getSieveCounts.fulfilled, (state, action) => {
        const { success, data } = action.payload || {};
        if (success && data) state.counts = data;
      })
      .addCase(getSieveCounts.rejected, () => {});

    builder
      .addCase(getSieveLogs.pending, (state) => {
        state.sieveLogsLoading = true;
        state.sieveLogs = [];
      })
      .addCase(getSieveLogs.fulfilled, (state, action) => {
        state.sieveLogsLoading = false;
        const { success, data } = action.payload || {};
        if (success) state.sieveLogs = data || [];
      })
      .addCase(getSieveLogs.rejected, (state) => {
        state.sieveLogsLoading = false;
      });

    builder
      .addCase(bulkCleanSieves.pending, (state) => {
        state.loading = true;
      })
      .addCase(bulkCleanSieves.fulfilled, (state, action) => {
        state.loading = false;
        const { success, message: msg } = action.payload || {};
        if (success) message.success(msg);
        else if (msg) message.error(msg);
      })
      .addCase(bulkCleanSieves.rejected, (state, action) => {
        state.loading = false;
        message.error(action.error?.message);
      });

    builder
      .addCase(bulkAllocateSieves.pending, (state) => {
        state.loading = true;
      })
      .addCase(bulkAllocateSieves.fulfilled, (state, action) => {
        state.loading = false;
        const { success, message: msg } = action.payload || {};
        if (success) message.success(msg);
        else if (msg) message.error(msg);
      })
      .addCase(bulkAllocateSieves.rejected, (state, action) => {
        state.loading = false;
        message.error(action.error?.message);
      });

    builder
      .addCase(bulkStatusChangeSieves.pending, (state) => {
        state.loading = true;
      })
      .addCase(bulkStatusChangeSieves.fulfilled, (state, action) => {
        state.loading = false;
        const { success, message: msg } = action.payload || {};
        if (success) message.success(msg);
        else if (msg) message.error(msg);
      })
      .addCase(bulkStatusChangeSieves.rejected, (state, action) => {
        state.loading = false;
        message.error(action.error?.message);
      });

    builder
      .addCase(generateSieveUsageLogReport.pending, (state) => {
        state.reportGenerating = true;
      })
      .addCase(generateSieveUsageLogReport.fulfilled, (state, action) => {
        state.reportGenerating = false;
        const { success, data, message: msg } = action.payload || {};
        if (success && data) {
          const filename = String(data).split(/[?#]/)[0].split('/').pop();
          window.open(
            `${process.env.REACT_APP_STORAGE_URL}${filename}`,
            '_blank'
          );
          message.success('Report generated successfully.');
        } else if (msg) {
          message.error(msg);
        }
      })
      .addCase(generateSieveUsageLogReport.rejected, (state, action) => {
        state.reportGenerating = false;
        message.error(action.error?.message);
      });

    builder
      .addCase(bulkInactivateSieves.pending, (state) => {
        state.loading = true;
      })
      .addCase(bulkInactivateSieves.fulfilled, (state, action) => {
        state.loading = false;
        const { success, message: msg } = action.payload || {};
        if (success) message.success(msg);
        else if (msg) message.error(msg);
      })
      .addCase(bulkInactivateSieves.rejected, (state, action) => {
        state.loading = false;
        const msg = action.payload?.message || action.error?.message;
        if (msg) message.error(msg);
      });

    builder
      .addCase(reactivateSieve.pending, (state) => {
        state.loading = true;
      })
      .addCase(reactivateSieve.fulfilled, (state, action) => {
        state.loading = false;
        const { success, message: msg } = action.payload || {};
        if (success) message.success(msg);
        else if (msg) message.error(msg);
      })
      .addCase(reactivateSieve.rejected, (state, action) => {
        state.loading = false;
        const msg = action.payload?.message || action.error?.message;
        if (msg) message.error(msg);
      });
  }
});

export default sieveLabelSlice.reducer;
